import { apiClient } from '../../config/axios-client'
import type { GenerationStatus } from '../../shared/enums/GenerationStatus'
import type { UpsertReferenceAsset } from '../../shared/models/ReferenceAsset'
import type { Shot, ShotPosition, ShotTree, UpsertShot } from '../../shared/models/Shot'
import type { GenerationParams } from '../types/GenerationParams'
import type { ListParams } from '../types/ListParams'

export const shotsApi = {
  list: (commercialId: string) => apiClient.get<ShotTree[]>(`commercials/${commercialId}/shots`),

  reorder: (commercialId: string, data: ShotPosition[]) =>
    apiClient.patch<unknown>(`commercials/${commercialId}/technical-scouts/scenes/ordering`, {
      technicalScouts: data,
    }),

  detail: (commercialId: string, shotId: string, variantId?: string) =>
    apiClient.get<Shot>(
      `commercials/${commercialId}/shots/${shotId}/variants${variantId ? `/${variantId}` : ''}`,
    ),

  create: (commercialId: string, data: UpsertShot) =>
    apiClient.post<{ id: string }>(`commercials/${commercialId}/shots`, data),

  createVariant: (commercialId: string, shotId: string, data: UpsertShot) =>
    apiClient.post<{ id: string }>(`commercials/${commercialId}/shots/${shotId}/variants`, data),

  update: (commercialId: string, shotId: string, variantId: string, data: UpsertShot) =>
    apiClient.patch<unknown>(
      `commercials/${commercialId}/shots/${shotId}/variants/${variantId}`,
      data,
    ),

  delete: (commercialId: string, shotId: string, variantId?: string) =>
    apiClient.delete<unknown>(
      `commercials/${commercialId}/shots/${shotId}${variantId ? `/variants/${variantId}` : ''}`,
    ),

  setActive: (commercialId: string, shotId: string, variantId: string) =>
    apiClient.put<unknown>(
      `commercials/${commercialId}/shots/${shotId}/active-version/${variantId}`,
    ),

  setImage: (commercialId: string, shotId: string, variantId: string, imageId: string) =>
    apiClient.patch<unknown>(
      `commercials/${commercialId}/shots/${shotId}/variants/${variantId}/reference-shot`,
      { imageId },
    ),

  deleteImages: (commercialId: string, imagePaths: string[]) =>
    apiClient.delete<unknown>(`commercials/${commercialId}/shots/images`, {
      data: { imagePaths },
    }),

  generateImages: (
    commercialId: string,
    shotId: string,
    variantId: string,
    params?: GenerationParams,
  ) =>
    apiClient.post<unknown>(
      `commercials/${commercialId}/shots/${shotId}/variants/${variantId}/generate-image`,
      params,
    ),

  generateImagesStatus: (commercialId: string, shotId: string, variantId: string) =>
    apiClient.get<{ status: GenerationStatus }>(
      `commercials/${commercialId}/shots/${shotId}/variants/${variantId}/status`,
    ),

  references: (commercialId: string, shotId: string, variantId: string, params: ListParams) =>
    apiClient.get<unknown>(
      `commercials/${commercialId}/shots/${shotId}/variants/${variantId}/references`,
      { params },
    ),

  referencesCount: (commercialId: string, shotId: string, variantId: string) =>
    apiClient.get<number>(
      `commercials/${commercialId}/shots/${shotId}/variants/${variantId}/references/count`,
    ),

  createReference: (
    commercialId: string,
    shotId: string,
    variantId: string,
    params: UpsertReferenceAsset,
  ) => {
    const formData = new FormData()
    Object.entries(params).forEach(([key, value]) => {
      formData.append(key, value instanceof File ? value : String(value))
    })
    return apiClient.post<unknown>(
      `commercials/${commercialId}/shots/${shotId}/variants/${variantId}/references`,
      formData,
    )
  },

  updateReference: (
    commercialId: string,
    shotId: string,
    variantId: string,
    referenceId: string,
    params: Partial<UpsertReferenceAsset>,
  ) => {
    const formData = new FormData()
    Object.entries(params).forEach(([key, value]) => {
      formData.append(key, value instanceof File ? value : String(value))
    })
    return apiClient.patch<unknown>(
      `commercials/${commercialId}/shots/${shotId}/variants/${variantId}/references/${referenceId}`,
      formData,
    )
  },

  deleteReference: (commercialId: string, shotId: string, variantId: string, referenceId: string) =>
    apiClient.delete<unknown>(
      `commercials/${commercialId}/shots/${shotId}/variants/${variantId}/references/${referenceId}`,
    ),
}
